import type { Infer } from 'superstruct';
import {
	any,
	array,
	assign,
	number,
	object,
	optional,
	string,
	union,
} from 'superstruct';

import { ListResourceDto, PaginationQuery } from '@adflow/shared/util-data';
import { adflowApiSchema } from '@adflow/shared/util-route-schema';
import { parsableInteger } from '@adflow/shared/util-superstruct';

import { ViewGMBLocationDto } from './gmb-location.dto';
import {
	AddGMBTemplateDto,
	EditGMBTemplateDto,
	ScheduleGMBTemplateDto,
	ViewGMBTemplateDto,
} from './gmb-template.dto';
import { ListGMBTemplateQueryDto } from './list-gmb-template-query.dto';

const p = adflowApiSchema.prefix('google-my-business');

export const ListGoogleAdsTemplatesQuery = assign(
	PaginationQuery,
	ListGMBTemplateQueryDto,
	object({ search: optional(string()) })
);

export type ListGoogleAdsTemplatesQuery = Infer<
	typeof ListGoogleAdsTemplatesQuery
>;

export const ListGoogleMyBusinesss = p.get('templates', {
	req: object({
		query: ListGMBTemplateQueryDto,
	}),
	res: ListResourceDto(ViewGMBTemplateDto),
});

export const GetGoogleMyBusiness = p.get('templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: ViewGMBTemplateDto,
});

export const GetGoogleMyBusinessLocations = p.get('locations', {
	req: object({}),
	res: array(ViewGMBLocationDto),
});

export const AddGoogleMyBusiness = p.post('templates', {
	req: object({
		body: AddGMBTemplateDto,
	}),
	res: ViewGMBTemplateDto,
});

export const EditGoogleMyBusiness = p.patch('templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
		body: EditGMBTemplateDto,
	}),
	res: ViewGMBTemplateDto,
});

export const DeleteGoogleMyBusinessTemplate = p.post(
	'templates/:templateId/delete',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: object({}),
	}
);

export const DeleteGoogleMyBusinessTemplates = p.delete('templates/delete', {
	req: object({
		body: object({ templateIds: array(parsableInteger()) }),
	}),
	res: object({}),
});

export const UploadGoogleMyBusinessTemplate = p.post(
	'templates/:templateId/upload',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: any(),
	}
);

export const ScheduleGoogleMyBusinessTemplate = p.post(
	'templates/:templateId/schedule',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			body: ScheduleGMBTemplateDto,
		}),
		res: ViewGMBTemplateDto,
	}
);

export const CancelScheduledGoogleMyBusinessTemplate = p.post(
	'templates/:templateId/cancel-schedule',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: ViewGMBTemplateDto,
	}
);

const ValidationErrorResponse = object({
	id: optional(number()),
	field: string(),
	type: string(),
	message: string(),
});

export const CheckGMBTemplateUniqueNamesValidation = p.post(
	'validate_unique_names',
	{
		req: object({
			body: union([AddGMBTemplateDto, EditGMBTemplateDto]),
		}),
		res: object({
			validateErrors: array(ValidationErrorResponse),
		}),
	}
);

export const SyncGMBAccounts = p.post('sync-accounts', {
	req: object({}),
	res: object({}),
});
